package com.trevorwiebe.timesheet.loads.presentation.admin

import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.domain.model.LoadStop
import com.trevorwiebe.timesheet.loads.domain.model.drops
import com.trevorwiebe.timesheet.loads.domain.model.pickups
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.mileage.domain.model.cityState

/**
 * Builds the plain-text dispatch summary shared from the Completed Loads screen, e.g.:
 * ```
 * Wednesday, July 1
 * Dave- Heifer Source (Wichita, KS) to Hillsboro Feed (Hillsboro, KS)
 *
 * Thursday, July 2
 * Nathan- Blue Sky (Pratt, KS) to Busenitz Farm (Whitewater, KS)
 * ```
 */
object CompletedLoadsShareText {

    fun build(
        loads: List<Load>,
        locations: List<Location>,
        users: List<DatabaseUserModel>,
    ): String {
        val byDate = loads
            .filter { it.scheduledDate != null }
            .sortedBy { it.scheduledDate!! }
            .groupBy { it.scheduledDate!! }

        return byDate.entries.joinToString("\n\n") { (date, dayLoads) ->
            buildString {
                append(Util.toDayOfWeekMonthDay(date))
                dayLoads.forEach { load ->
                    append('\n')
                    append(lineFor(load, locations, users))
                }
            }
        }
    }

    private fun lineFor(
        load: Load,
        locations: List<Location>,
        users: List<DatabaseUserModel>,
    ): String {
        val driver = users.firstOrNull { it.id == load.employeeId }?.displayName ?: "Unknown"
        val pickups = load.pickups.joinToString(", ") { stopText(it, locations) }
        val drops = load.drops.joinToString(", ") { stopText(it, locations) }
        val route = listOf(pickups, drops).filter { it.isNotEmpty() }.joinToString(" to ")
        return "$driver- $route"
    }

    private fun stopText(stop: LoadStop, locations: List<Location>): String {
        val location = locations.firstOrNull { it.id == stop.locationId }
        val cityState = location?.cityState.orEmpty()
        val name = location?.name ?: "—"
        return if (cityState.isEmpty()) name else "$name ($cityState)"
    }
}
